import React from 'react';
import PropTypes from 'prop-types';
import Slider from '@mui/material/Slider';

import Immutable from 'immutable';
import moment from 'moment-business-days';
import _ from 'lodash';
import { Grid, Select, Checkbox, Tabs } from '@ts/storybook';
import { withTranslation } from 'react-i18next';
import { TextInput, WrapperEntitySelector, DateTimeInput } from '@components/Inputs';
import Misc from '@components/Misc';
import StatusPage from '../Status/StatusInput';
import UserLabelsSelection from '@components/Inputs/UserLabelsSelection';
import ItemImagesTab from '@pages/DataBase/Documents/DocumentTab';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';

class TaskForm extends React.Component {
  static displayName = 'TaskForm';

  static propTypes = {
    app: PropTypes.object.isRequired,
    data: PropTypes.object.isRequired,
    mode: PropTypes.string.isRequired,
    values: PropTypes.object.isRequired,
    rsrc: PropTypes.object,
  };

  state = {
    activeTab: 'info',
    calendarDays: true,
  };

  _handleTabChange = (event, tab) => {
    return this.setState({ activeTab: tab });
  };

  componentDidMount() {
    const { values } = this.props;
    if (values) {
      const dueTimestamp = values.dueTimestamp;
      if (dueTimestamp) {
        values.dueDate = new Date(dueTimestamp);
        return (values.dueTime = new Date(dueTimestamp));
      }
    }
  }

  render() {
    this._curValues = this.props.values;
    this._mode = this.props.mode;
    this._fDisabled = this._mode === 'view';

    const tabs = [
      { label: 'Task Information', value: 'info' },
      { label: 'Related Items', value: 'related' },
      { label: 'Events and Rules', value: 'events' },
      {
        label: 'Images',
        value: 'images',
      },
    ];
    return (
      <Tabs id={'task-form'} tabs={tabs} value={this.state.activeTab} onChange={this._handleTabChange}>
        {this._renderContents()}
      </Tabs>
    );
  }

  _renderContents = () => {
    const contents = (() => {
      switch (this.state.activeTab) {
        case 'info':
          return this._renderMain();
        case 'related':
          return this._renderRelated();
        case 'events':
          return this._renderEventsAndRules();
        case 'images':
          return (
            <ItemImagesTab
              {...this.props}
              entityTypeId="taTask"
              entityId={this.props.values.id}
              documents={
                this.props.values.documents.toJS ? this.props.values.documents.toJS() : this.props.values.documents
              }
            />
          );
      }
    })();
    return contents;
  };

  _renderMain = () => {
    return (
      <Grid container size={12} spacing={2}>
        <Grid size={{ xs: 6 }}>{this._renderMainInfo()}</Grid>
        <Grid container direction={'column'} size={{ xs: 6 }} spacing={2}>
          <Grid size="grow">{this._renderRecordTimestamp()}</Grid>
          <Grid size="grow">{this._renderDueTimestamp()}</Grid>
        </Grid>
      </Grid>
    );
  };

  _renderStatusPage = () => {
    const { currentStatus } = this._curValues;
    const currentIndicators = currentStatus != null ? Immutable.fromJS(currentStatus.indicators) : Immutable.List();
    return (
      <div style={{ marginTop: '25px', overflowX: 'auto' }}>
        <StatusPage
          app={this.props.app}
          data={this.props.data}
          mode={this.props.mode}
          title={this.props.t('Status')}
          statesList={currentIndicators}
          onChangeList={newList => {
            const newStatus = _.clone(currentStatus);
            if (newStatus) newStatus.indicators = newList?.toJS();
            return this.onChangeValue('currentStatus')(newStatus);
          }}
        />
      </div>
    );
  };

  _renderMainInfo = () => {
    const responsibleUserEntity = this._curValues.responsibleUser;
    let disabledResponsibleUserEntity;
    if (typeof this._curValues?.taskType === 'function') {
      disabledResponsibleUserEntity = this._curValues?.taskType.get('responsibleUser');
    } else {
      disabledResponsibleUserEntity = this._curValues?.taskType?.responsibleUser;
    }

    return (
      <Misc.NewPaperWithPadding>
        <Misc.PaperSectionTitle>{this.props.t('Main info')}</Misc.PaperSectionTitle>
        <br />
        <TextInput
          label="Description"
          value={this._curValues.description}
          onChangeValue={this.onChangeValue('description')}
          isReadOnly={this._fDisabled}
          disabled={this._fDisabled}
          required={this.props.requiredFields.includes('description')}
        />
        <TextInput
          label="Custom ID"
          value={this._curValues.customId}
          onChangeValue={this.onChangeValue('customId')}
          isReadOnly={this._fDisabled}
          disabled={this._fDisabled}
          infoText="Blank spaces at the beggining and the end will be trimmed on save"
          type="ID"
        />
        <WrapperEntitySelector
          searchComponent={generateSearchEntitySelector('taskType')}
          onChangeEntity={taskType => this.onChangeTaskType(taskType)}
          value={this._curValues.taskType}
          label={'Task type'}
          isReadOnly={this._fDisabled}
        />
        <WrapperEntitySelector
          searchComponent={generateSearchEntitySelector('user')}
          onChangeEntity={entity => this._handleChangeEntitySelector(entity, 'responsibleUser', 'responsibleUserId')}
          value={responsibleUserEntity}
          label={'Assignee'}
          isReadOnly={this._fDisabled}
          disabled={Boolean(disabledResponsibleUserEntity)}
        />
        <UserLabelsSelection
          label={'Addressed to following user groups'}
          value={this.props.values.userGroups}
          onChange={this.onChangeValue('userGroups')}
          isReadOnly={this._fDisabled}
        />
        <Misc.FlexRow>
          <Misc.FlexRowFlexItem>
            <Checkbox
              label="Is closed"
              className="mt-[30px]"
              checked={this._curValues.closed}
              onChangeValue={v => this.props.onChange({ path: 'closed', value: v })}
              isReadOnly={this._fDisabled}
              disabled={this._fDisabled}
              labelPosition="start"
            />
          </Misc.FlexRowFlexItem>
          <Misc.FlexRowSpacer />
          <Misc.FlexRowFlexItem>
            <Slider
              style={{ marginTop: '30px' }}
              onChange={(event, value) =>
                this.props.onChange({
                  path: 'percentageCompletion',
                  value: value,
                })
              }
              value={this._curValues.percentageCompletion}
              min={0}
              max={100}
              step={10}
              disabled={this._fDisabled}
            />
          </Misc.FlexRowFlexItem>
          <Misc.FlexRowSpacer />
          <Misc.FlexRowFlexItem>
            <p style={_style.sliderLabel}>{`${this.props.t('Completed at ')}${this._curValues.percentageCompletion}${this.props.t(
              '%'
            )}`}</p>
          </Misc.FlexRowFlexItem>
        </Misc.FlexRow>
        {this._renderStatusPage()}
        <br />
        {this._renderExtraInfo()}
      </Misc.NewPaperWithPadding>
    );
  };

  _renderExtraInfo = () => {
    return (
      <div>
        <WrapperEntitySelector
          label="Organization"
          value={this._curValues.organization}
          onChangeEntity={v => this._handleChangeEntitySelector(v, 'organization', 'ownerOrganizationId')}
          searchComponent={generateSearchEntitySelector('organization')}
          disabled={this._fDisabled}
        />
        <TextInput
          multiline
          label="Notes"
          value={this._curValues.notes}
          onChangeValue={this.onChangeValue('notes')}
          isReadOnly={this._fDisabled}
          disabled={this._fDisabled}
        />
      </div>
    );
  };

  onChangeOrganization = model => {
    const value = model ? model.get('id') : null;
    this.onChangeValue('ownerOrganization')(model);
    return this.onChangeValue('ownerOrganizationId')(value);
  };

  _renderRecordTimestamp = () => {
    let recordDate = null;
    if (this._curValues.recordDate) {
      recordDate = this._curValues.recordDate instanceof Date ? this._curValues.recordDate : new Date(this._curValues.recordDate);
    }

    return (
      <Misc.NewPaperWithPadding>
        <Misc.PaperSectionTitle>{this.props.t('Record date')}</Misc.PaperSectionTitle>
        <br />
        <Misc.FlexRow>
          <DateTimeInput
            isReadOnly={this._fDisabled}
            disabled={this._fDisabled}
            onChange={date => this.props.onChange({ path: 'recordDate', value: date })}
            value={recordDate}
            width="95%"
            labelDate={this.props.t('Date')}
            labelTime={this.props.t('Time')}
          />
        </Misc.FlexRow>
        <br />
        <Misc.FlexRow>
          <Misc.FlexRowFlexItem>
            <TextInput
              type="number"
              label="Due from record date"
              value={String(this._curValues.dueFromRecord)}
              onChangeValue={this._handleChangeDueFromRecord}
              isReadOnly={this._fDisabled}
              disabled={this._fDisabled}
            />
          </Misc.FlexRowFlexItem>
          <Misc.FlexRowFlexItem
            style={{
              display: 'flex',
              alignContent: 'flex-end',
              flexWrap: 'wrap',
            }}
          >
            <Select
              value={this._curValues.dueFromRecordUnits}
              onChange={this._handleChangeDueFromRecordUnits}
              isReadOnly={this._fDisabled}
              widthOptions
              options={[
                { label: 'days', value: 'days' },
                { label: 'hours', value: 'hours' },
              ]}
            />
          </Misc.FlexRowFlexItem>
        </Misc.FlexRow>
        <Misc.FlexRow>
          <Misc.FlexRowFlexItem>
            <Checkbox
              label="Use calendar days"
              style={this._checkHide({ paddingTop: '1rem' })}
              checked={this._curValues.calendarDays || false}
              onChangeValue={this._handleChangeCalendarDays}
              disabled={this.fDisabled}
              labelPosition="start"
            />
          </Misc.FlexRowFlexItem>
        </Misc.FlexRow>
      </Misc.NewPaperWithPadding>
    );
  };

  _handleChangeCalendarDays = calendarDays => {
    this.props.onChange({ path: 'calendarDays', value: calendarDays });
    const { dueFromRecordUnits, dueFromRecord } = this._curValues;
    this._computeDue({ dueFromRecordUnits, dueFromRecord, calendarDays });
  };

  _renderDueTimestamp = () => {
    let dueDate = null;
    if (this._curValues.dueDate != null) {
      dueDate = this._curValues.dueDate instanceof Date ? this._curValues.dueDate : new Date(this._curValues.dueDate);
    }

    return (
      <Misc.NewPaperWithPadding>
        <Misc.PaperSectionTitle>{this.props.t('Due date')}</Misc.PaperSectionTitle>
        <br />
        <Misc.FlexRow>
          <DateTimeInput
            isReadOnly={this._fDisabled}
            disabled={this._fDisabled}
            onChange={date => this.props.onChange({ path: 'dueDate', value: date })}
            value={dueDate}
            width="95%"
            labelDate={this.props.t('Due date')}
            labelTime={this.props.t('Due time')}
          />
        </Misc.FlexRow>
        <br />
        {this._renderWarningBefore()}
      </Misc.NewPaperWithPadding>
    );
  };

  _renderWarningBefore = () => {
    return (
      <Misc.FlexRow>
        <Misc.FlexRowFlexItem>
          <TextInput
            label="Warning before"
            value={String(this._curValues.warningBefore)}
            onChangeValue={this.onChangeValue('warningBefore')}
            isReadOnly={this._fDisabled}
            disabled={this._fDisabled}
            type="number"
          />
        </Misc.FlexRowFlexItem>
        <Misc.FlexRowFlexItem
          style={{
            display: 'flex',
            alignContent: 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          <Select
            value={this._curValues.warningBeforeUnits}
            onChange={this.onChangeValue('warningBeforeUnits')}
            isReadOnly={this._fDisabled}
            widthOptions
            options={[
              { label: 'days', value: 'days' },
              { label: 'hours', value: 'hours' },
            ]}
          />
        </Misc.FlexRowFlexItem>
      </Misc.FlexRow>
    );
  };

  _renderCheckBoxes = () => {
    return (
      <Misc.NewPaperWithPadding>
        <Checkbox
          disabled={this.fDisabled}
          checked={this._curValues.isMandatory}
          onChangeValue={v => this.props.onChange({ path: 'isMandatory', value: v })}
          label="Is mandatory"
          labelPosition="start"
        />
        <Checkbox
          disabled={this.fDisabled}
          label="Is recurrent"
          checked={this._curValues.isRecurrent}
          onChangeValue={v => this.props.onChange({ path: 'isRecurrent', value: v })}
          className="hidden"
          labelPosition="start"
        />
      </Misc.NewPaperWithPadding>
    );
  };

  _renderSeparatedTaskByItemType() {
    if (!this._curValues.separatedTask) {
      return null;
    }
    //TODO FIX
    return (
      <WrapperEntitySelector
        label="Restrict (only applicable) to a specific item type"
        value={this._curValues.separatedTaskItemTypeId}
        onChangeEntity={v => this._handleChangeEntitySelector(v, 'separatedTaskItemType', 'separatedTaskItemTypeId')}
        searchComponent={generateSearchEntitySelector('itemType')}
        disabled={this._fDisabled || this._mode !== 'new'}
      />
    );
    /* return (
      <EntitySelector
        isReadOnly={this._fDisabled || this._mode !== 'new'}
        value={this._curValues.separatedTaskItemTypeId}
        onEntitySelected={entity => this._handleChangeEntitySelector(entity, 'separatedTaskItemType', 'separatedTaskItemTypeId')}
        app={this.props.app}
        data={this.props.data}
        searchComponent={ItemTypes}
        label={this.props.t('Restrict (only applicable) to a specific item type')}
        verificationURL="/api/itemTypes"
        logicalLink="itemType"
        selectedEntity={this._curValues.separatedTaskItemType}
        fieldsToSearch={['logicalId', 'name', 'customId']}
      />
    ); */
  }

  _renderEventLogging = () => {
    return (
      <div>
        <Misc.PaperSectionTitle>{this.props.t('Event logging')}</Misc.PaperSectionTitle>
        <br />
        <Misc.FlexRow>
          <Misc.FlexRowFlexItem>
            <Checkbox
              label="Log event upon status change"
              checked={this._curValues.logEventUponStatusChange}
              onChangeValue={this.onChangeValue('logEventUponStatusChange')}
              className="mt-6"
              isReadOnly={this._fDisabled}
              fDisabled={this._fDisabled}
            />
          </Misc.FlexRowFlexItem>
          <Misc.FlexRowSpacer />
          <Misc.FlexRowFlexItem>
            <WrapperEntitySelector
              searchComponent={generateSearchEntitySelector('eventType')}
              isReadOnly={this._fDisabled || !this._curValues.logEventUponStatusChange}
              value={this._curValues.statusChangeEventType}
              onChangeEntity={entity =>
                this._handleChangeEntitySelector(entity, 'statusChangeEventType', 'statusChangeEventTypeId')
              }
              label={'Status change event type'}
            />
          </Misc.FlexRowFlexItem>
        </Misc.FlexRow>
      </div>
    );
  };

  _renderRules = () => {
    return (
      <div>
        <Misc.PaperSectionTitle>{this.props.t('Rules')}</Misc.PaperSectionTitle>
        <br />
        <Misc.FlexRow>
          <Misc.FlexRowFlexItem>
            <Checkbox
              label="Create successive task upon completion of every task"
              checked={this._curValues.successiveTask}
              onChangeValue={this.onChangeValue('successiveTask')}
              disabled={this._fDisabled || this._curValues.taskTypeId != null}
            />
          </Misc.FlexRowFlexItem>
        </Misc.FlexRow>
      </div>
    );
  };

  _renderRelated = () => {
    return (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Misc.NewPaperWithPadding>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>{this._renderRelatedItems()}</Grid>
              <Grid size={{ xs: 12 }}>{this._renderRelatedPeople()}</Grid>
            </Grid>
          </Misc.NewPaperWithPadding>
        </Grid>
      </Grid>
    );
  };

  _renderRelatedItems() {
    const relatedItems = this._curValues.relatedItems;

    return (
      <>
        <Misc.PaperSectionTitle key="title" style={{ marginBottom: '10px' }}>
          {this.props.t('Related Items')}
        </Misc.PaperSectionTitle>
        {this.props.mode === 'new' && (
          <>
            <Checkbox
              key="chkbx"
              label="Create an independent task for each item"
              checked={this._curValues.separatedTask}
              disabled={this.fDisabled}
              onChangeValue={v => this.props.onChange({ path: 'separatedTask', value: v })}
              labelPosition="start"
            />
            {this._renderSeparatedTaskByItemType()}
          </>
        )}
        {this.props.mode === 'new' && <br />}
        <WrapperEntitySelector
          multiple
          label="Items"
          value={relatedItems}
          onChange={this.onChangeValue('relatedItems')}
          searchComponent={generateSearchEntitySelector('item')}
          isReadOnly={this._fDisabled}
        />
      </>
    );
  }

  _itemColumns() {
    return [
      {
        fnLabel: () => `${this.props.t('ID')}`,
        name: 'id',
        fHidden: true,
      },
      {
        fnLabel: () => `${this.props.t('Name')}`,
        name: 'name',
        rawValue: o => {
          return o.get('name');
        },
      },
    ];
  }
  _peopleColumns() {
    return [
      {
        fnLabel: () => `${this.props.t('ID')}`,
        name: 'id',
        fHidden: true,
      },
      {
        fnLabel: () => `${this.props.t('Name')}`,
        name: 'name',
        rawValue: o => {
          const personData = o.getIn(['flavors', 'person', 'personData']);
          return personData ? `${personData.get('firstName')} ${personData.get('familyName')}` : o.get('name');
        },
      },
    ];
  }

  _renderRelatedPeople = () => {
    return (
      <>
        <Misc.PaperSectionTitle key="title" style={{ marginBottom: '10px' }}>
          {this.props.t('Related People')}
        </Misc.PaperSectionTitle>
        <WrapperEntitySelector
          multiple
          label="People"
          value={this.props.values.relatedPeople}
          onChange={this.onChangeValue('relatedPeople')}
          searchComponent={generateSearchEntitySelector('people')}
          isReadOnly={this._fDisabled}
        />
      </>
    );
  };

  _renderEventsAndRules = () => {
    return (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Misc.NewPaperWithPadding>{this._renderRules()}</Misc.NewPaperWithPadding>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Misc.NewPaperWithPadding>{this._renderEventLogging()}</Misc.NewPaperWithPadding>
        </Grid>
      </Grid>
    );
  };

  onChangeValue = attr => value => this.props.onChange({ path: attr, value: value });

  onChangeTaskType = taskType => {
    const tt = taskType;

    let value = null;
    if (tt != null) {
      value = tt.getID();
      const logEventUponStatusChange = tt.logEventUponStatusChange;
      const statusChangeEventTypeId = tt.statusChangeEventTypeId;
      if (logEventUponStatusChange && statusChangeEventTypeId) {
        this.props.onChange({
          path: 'logEventUponStatusChange',
          value: logEventUponStatusChange,
        });
        this.props.onChange({
          path: 'statusChangeEventTypeId',
          value: statusChangeEventTypeId,
        });
      }

      const dueFromRecordUnits = tt.periodicityUnits;
      const dueFromRecord = tt.periodicity;
      const calendarDays = tt.periodicityCalendarDays;
      if (dueFromRecordUnits && dueFromRecord) {
        this.props.onChange({
          path: 'dueFromRecordUnits',
          value: dueFromRecordUnits,
        });
        this.props.onChange({
          path: 'dueFromRecord',
          value: dueFromRecord,
        });
        this._computeDue({ dueFromRecordUnits, dueFromRecord, calendarDays });
      }

      if (calendarDays != null) {
        this.props.onChange({ path: 'calendarDays', value: calendarDays });
      }
      if (tt.userGroups) {
        this.props.onChange({
          path: 'userGroups',
          value: tt.userGroups,
        });
      }
      if (tt.responsibleUser) {
        this._handleChangeEntitySelector(tt.responsibleUser, 'responsibleUser', 'responsibleUserId');
        //this.onChangeResponsibleUser(tt.responsibleUser');
      }
      this.props.onChange({
        path: 'separatedTask',
        value: tt.separatedTask,
      });
      this.props.onChange({
        path: 'successiveTask',
        value: tt.successiveTask,
      });
      this.props.onChange({
        path: 'warningBefore',
        value: tt.warningBefore,
      });
      this.props.onChange({
        path: 'warningBeforeUnits',
        value: tt.warningBeforeUnits,
      });
    }
    this.props.onChange({ path: 'taskTypeId', value });
    this.props.onChange({ path: 'taskType', value: tt });
  };

  _handleChangeDueFromRecord = dueFromRecord => {
    this.props.onChange({ path: 'dueFromRecord', value: dueFromRecord });
    const { dueFromRecordUnits, calendarDays } = this._curValues;
    this._computeDue({ dueFromRecord, dueFromRecordUnits, calendarDays });
  };

  _handleChangeDueFromRecordUnits = value => {
    const dueFromRecordUnits = value;
    const { calendarDays, dueFromRecord } = this._curValues;
    this._computeDue({ dueFromRecordUnits, dueFromRecord, calendarDays });
    this.props.onChange({
      path: 'dueFromRecordUnits',
      value: dueFromRecordUnits,
    });
  };

  _computeDue({ dueFromRecordUnits, dueFromRecord, calendarDays = true }) {
    const recordDate = moment(this._curValues.recordDate);
    const recordTime = moment(this._curValues.recordTime);
    const units = dueFromRecordUnits;
    if (!recordDate || !units) return;

    let dueDate = moment(recordDate);
    let dueTime = moment(recordTime);
    if (units == 'hours') {
      dueTime.add(dueFromRecord, 'h');
    } else if (units == 'days') {
      calendarDays ? dueDate.add(dueFromRecord, 'd') : dueDate.businessAdd(dueFromRecord);
    }

    this.props.onChange({
      path: 'dueDate',
      value: new Date(dueDate.format()),
    });
    this.props.onChange({
      path: 'dueTime',
      value: new Date(dueTime.format()),
    });
  }

  _handleChangeEntitySelector = (entity, entityName, fkField) => {
    const value = entity ? entity.getID() : null;
    this.props.onChange({ path: entityName, value: entity });
    this.props.onChange({ path: fkField, value });
  };

  _checkHide = (style = null) => {
    if (this.props.mode === 'view') {
      if (style != null) {
        return _.merge({ display: 'none' }, style);
      }
      return { display: 'none' };
    }
    return style;
  };

  _handleClickAddItem = itemArray => {
    this._curValues[itemArray] = this._curValues[itemArray].push(null);
    this.props.onChange({ path: itemArray, value: this._curValues[itemArray] });
  };

  _handleClickRemoveItem = itemArray => {
    this._curValues[itemArray] = this._curValues[itemArray].pop();
    this.props.onChange({ path: itemArray, value: this._curValues[itemArray] });
  };

  _changeRelatedItem = (idx, itemArray) => {
    return value => {
      this._curValues[itemArray] = this._curValues[itemArray].set(idx, value);
      return this.props.onChange({
        path: itemArray,
        value: this._curValues[itemArray],
      });
    };
  };
}

const _style = {
  outer: {},
  tabWrapper: {
    paddingTop: 10,
  },
  buttonAddItem: {
    textAlign: 'center',
    marginBottom: 5,
  },
  block: {
    minHeight: 200,
  },
  checkbox: {
    marginBottom: 16,
    marginTop: 15,
  },
  sliderLabel: {
    fontSize: '12px',
    marginTop: '30px',
    marginLeft: '25px',
  },
  slider: {
    marginTop: '30px',
    marginBottom: '1px',
    marginLeft: '20px',
  },
};

export default withTranslation()(TaskForm);
